"use client";

import { Core } from "@faire/design-tokens";
import { usePaymentTerm } from "@faire/retailer-visitor-shared/hooks/retailer/usePaymentTerm";
import { useRetailer } from "@faire/retailer-visitor-shared/hooks/retailer/useRetailer";
import { SignupFormContainer } from "@faire/retailer-visitor-shared/views/SignUp/_components/CommonStyles";
import { CoreCard } from "@faire/retailer-visitor-shared/views/SignUp/_components/net-terms/CoreCard";
import { HeaderAnimationContainer } from "@faire/retailer-visitor-shared/views/SignUp/_components/net-terms/HeaderAnimationContainer";
import { UpmarketCard } from "@faire/retailer-visitor-shared/views/SignUp/_components/net-terms/UpmarketCard";
import { useRetailerUpmarket } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useRetailerUpmarket";
import { useSignUpEventTracker } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpEventTracker";
import { useSignUpUPLT } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpUPLT";
import {
  SccClickType,
  trackSccClick,
} from "@faire/retailer-visitor-shared/views/SignUp/limit-increase-scc/trackSccClick";
import { SecondaryAction } from "@faire/retailer-visitor-shared/views/SignUp/select-payment-terms/__internal__/SecondaryAction";
import { useSaveRequestAndGoToNextStep } from "@faire/retailer-visitor-shared/views/SignUp/signupStepper";
import { Step } from "@faire/retailer-visitor-shared/views/SignUp/Step";
import { getWebApiErrorMessage } from "@faire/web--source/common/getErrorMessage";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useMutation as useMutationSetPaymentTerm } from "@faire/web-api--source/endpoints/www/api/retailer/v2/set-payment-term/post-hooks";
import { ISetPaymentTermRequest } from "@faire/web-api--source/indigofair/data/ISetPaymentTermRequest";
import { PaymentTerm } from "@faire/web-api--source/indigofair/data/PaymentTerm";
import React, { useEffect } from "react";
import { useSccLimitIncreaseEligibility } from "../_hooks/useSccLimitIncreaseEligibility";
import { PrimaryAction } from "./__internal__/PrimaryAction";
import { Headers } from "./__internal__/Headers";
import { IdentityVerificationPolicy } from "../identity-verification/IdentityVerificationPolicy";

export const SelectNetTermsStep: React.FC = () => {
  const { saveAndGoToNextStep } = useSaveRequestAndGoToNextStep(
    Step.SELECT_NET_TERMS
  );
  const { trackFieldValue } = useSignUpEventTracker(Step.SELECT_NET_TERMS);

  const {
    assignSccLimitIncreaseExperiment,
    isTreatmentForSccLimitIncreaseExperiment,
  } = useSccLimitIncreaseEligibility();

  const [error, setError] = React.useState<string | undefined>(undefined);
  const { retailer } = useRetailer();
  const { maximumAvailablePaymentTerm } = usePaymentTerm();
  const { strictLocalize } = useStrictLocalization();
  const { mutateAsync: setPaymentTerm, isPending: isLoading } =
    useMutationSetPaymentTerm();
  const { isElegibleForUpmarket } = useRetailerUpmarket();
  const isUpmarketQualified = isElegibleForUpmarket(
    retailer?.annual_sales_min_amount,
    retailer?.reported_creation_time
  );
  useSignUpUPLT({ step: Step.SELECT_NET_TERMS, isLoading });

  const setupPaymentTerms = async (
    paymentTerm: keyof typeof PaymentTerm,
    startScc?: boolean
  ) => {
    try {
      await setPaymentTerm([
        ISetPaymentTermRequest.build({ payment_term: paymentTerm }),
      ]);
      trackFieldValue("payment_term", paymentTerm);

      saveAndGoToNextStep(undefined, { retailer, startScc });
    } catch (e) {
      setError(getWebApiErrorMessage(error, strictLocalize));
    }
  };

  const onSubmit = async (paymentTerm: keyof typeof PaymentTerm) => {
    await setupPaymentTerms(paymentTerm);
  };

  const startSccApplication = async () => {
    trackSccClick(SccClickType.START_SCC);
    await setupPaymentTerms(maximumAvailablePaymentTerm, true);
  };

  useEffect(() => {
    assignSccLimitIncreaseExperiment();
  }, [assignSccLimitIncreaseExperiment]);

  return (
    <SignupFormContainer>
      <HeaderAnimationContainer>
        <Headers
          isUpmarketQualified={isUpmarketQualified}
          isTreatmentForSccLimitIncreaseExperiment={isTreatmentForSccLimitIncreaseExperiment}
        />
        <Spacer height="4x" />
        {isUpmarketQualified ? <UpmarketCard /> : <CoreCard />}
        <Spacer height="9x" />
        <PrimaryAction
          isUpmarketQualified={isUpmarketQualified}
          isTreatmentForSccLimitIncreaseExperiment={isTreatmentForSccLimitIncreaseExperiment}
          isLoading={isLoading}
          onClickSccApplication={startSccApplication}
          onSubmit={() => onSubmit(maximumAvailablePaymentTerm)}
        />
        <SecondaryAction
          onSubmit={() => onSubmit(PaymentTerm.PAYMENT_ON_SHIPMENT)}
          isLoading={isLoading}
          isUpmarketQualified={isUpmarketQualified}
          isTreatmentForSccLimitIncreaseExperiment={isTreatmentForSccLimitIncreaseExperiment}
        />
        {error ? (
          <>
            <Spacer height="3x" />
            <Typography color={Core.text.critical}>{error}</Typography>
          </>
        ) : null}
        <Spacer height="3x" />
        <IdentityVerificationPolicy />
      </HeaderAnimationContainer>
    </SignupFormContainer>
  );
};
